import * as ToastPrimitive from "@radix-ui/react-toast";
import { AlertCircle, AlertTriangle, CheckCircle2, X as CloseIcon, Info } from "lucide-react";
import * as React from "react";
import { Button, type ButtonVariant } from "../atoms/button.js";
import { cx } from "../internal/cx.js";

export type ToastVariant = "info" | "success" | "warning" | "danger" | "neutral";

const ICON_BY_VARIANT: Record<ToastVariant, React.ElementType> = {
  info: Info,
  success: CheckCircle2,
  warning: AlertTriangle,
  danger: AlertCircle,
  neutral: Info,
};

/* ─── Primitives (re-exported wrappers around Radix) ─── */

export type ToastProviderProps = React.ComponentPropsWithoutRef<typeof ToastPrimitive.Provider>;

export function ToastProvider({
  duration = 5000,
  swipeDirection = "right",
  ...props
}: ToastProviderProps) {
  return <ToastPrimitive.Provider duration={duration} swipeDirection={swipeDirection} {...props} />;
}

export const ToastViewport = React.forwardRef<
  React.ElementRef<typeof ToastPrimitive.Viewport>,
  React.ComponentPropsWithoutRef<typeof ToastPrimitive.Viewport>
>(function ToastViewport({ className, ...props }, ref) {
  return (
    <ToastPrimitive.Viewport
      ref={ref}
      className={cx("cw-v2-theme cw-v2-toast__viewport", className)}
      {...props}
    />
  );
});
ToastViewport.displayName = "ToastViewport";

export interface ToastRootProps
  extends Omit<React.ComponentPropsWithoutRef<typeof ToastPrimitive.Root>, "type"> {
  variant?: ToastVariant;
}

export const ToastRoot = React.forwardRef<
  React.ElementRef<typeof ToastPrimitive.Root>,
  ToastRootProps
>(function ToastRoot({ className, variant = "neutral", ...props }, ref) {
  return (
    <ToastPrimitive.Root
      ref={ref}
      type={variant === "danger" || variant === "warning" ? "foreground" : "background"}
      className={cx("cw-v2-toast", `cw-v2-toast--${variant}`, className)}
      {...props}
    />
  );
});
ToastRoot.displayName = "ToastRoot";

export const ToastTitle = React.forwardRef<
  React.ElementRef<typeof ToastPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof ToastPrimitive.Title>
>(function ToastTitle({ className, ...props }, ref) {
  return (
    <ToastPrimitive.Title ref={ref} className={cx("cw-v2-toast__title", className)} {...props} />
  );
});
ToastTitle.displayName = "ToastTitle";

export const ToastDescription = React.forwardRef<
  React.ElementRef<typeof ToastPrimitive.Description>,
  React.ComponentPropsWithoutRef<typeof ToastPrimitive.Description>
>(function ToastDescription({ className, ...props }, ref) {
  return (
    <ToastPrimitive.Description
      ref={ref}
      className={cx("cw-v2-toast__description", className)}
      {...props}
    />
  );
});
ToastDescription.displayName = "ToastDescription";

export const ToastAction = React.forwardRef<
  React.ElementRef<typeof ToastPrimitive.Action>,
  React.ComponentPropsWithoutRef<typeof ToastPrimitive.Action>
>(function ToastAction({ className, ...props }, ref) {
  return (
    <ToastPrimitive.Action ref={ref} className={cx("cw-v2-toast__action", className)} {...props} />
  );
});
ToastAction.displayName = "ToastAction";

export const ToastClose = React.forwardRef<
  React.ElementRef<typeof ToastPrimitive.Close>,
  React.ComponentPropsWithoutRef<typeof ToastPrimitive.Close>
>(function ToastClose({ className, ...props }, ref) {
  return (
    <ToastPrimitive.Close
      ref={ref}
      aria-label="Dismiss"
      className={cx("cw-v2-toast__close", className)}
      {...props}
    >
      <CloseIcon className="cw-v2-toast__close-icon" aria-hidden />
    </ToastPrimitive.Close>
  );
});
ToastClose.displayName = "ToastClose";

/* ─── Convenience: Toast composed component + useToast hook ─── */

export interface ToastDescriptor {
  /**
   * Escape hatch for a fully custom trailing action node. Most consumers
   * should use the `actionLabel` + `onAction` shortcut below — it renders a
   * correctly-sized Button automatically. Use `action` only when you need
   * non-button UI (a Link, an avatar stack, etc.).
   */
  action?: React.ReactNode;
  /**
   * Label for the primary action button. When set, the toast renders a
   * Button at size sm inside a ToastAction wrapper, wired to onAction.
   * Action buttons in toasts are always size sm by design — actions inside a
   * compact notification should never compete with the title for visual
   * weight.
   */
  actionLabel?: React.ReactNode;
  /** Click handler for the primary action button. */
  onAction?: () => void;
  /** Visual treatment for the primary action button. Default 'secondary'. */
  actionVariant?: ButtonVariant;
  /**
   * Label for an optional secondary action, rendered to the left of the
   * primary action. Defaults to the Button `link` variant so the two
   * actions read as an explicit hierarchy (lighter alternative + filled
   * primary). Examples: "Undo" + "View details", "Dismiss" + "Retry".
   */
  secondaryActionLabel?: React.ReactNode;
  /** Click handler for the secondary action. */
  onSecondaryAction?: () => void;
  /** Visual treatment for the secondary action. Default 'link'. */
  secondaryActionVariant?: ButtonVariant;
  /** Description text or node. */
  description?: React.ReactNode;
  /** Auto-dismiss in ms. Set Infinity for sticky toasts. Defaults to provider duration. */
  duration?: number;
  /** Stable id. Pass to dismiss programmatically. */
  id?: string;
  /** Title text or node. */
  title?: React.ReactNode;
  variant?: ToastVariant;
}

interface InternalToast extends ToastDescriptor {
  id: string;
  open: boolean;
}

interface ToastContextValue {
  push: (toast: ToastDescriptor) => string;
  dismiss: (id: string) => void;
  dismissAll: () => void;
}

const ToastContext = React.createContext<ToastContextValue | null>(null);

/**
 * Mount once near the top of your app. Wraps Radix ToastProvider, renders the
 * viewport, and exposes a context for `useToast()` so any descendant can
 * imperatively spawn toasts without prop-drilling.
 */
export interface ToastHostProps {
  children?: React.ReactNode;
  /** Default auto-dismiss duration in ms. Default 5000. */
  duration?: number;
  /** Direction in which the user swipes to dismiss. Default 'right'. */
  swipeDirection?: "right" | "left" | "up" | "down";
  /** Custom render override for individual toasts. */
  renderToast?: (toast: InternalToast) => React.ReactNode;
}

export function ToastHost({
  children,
  duration = 5000,
  swipeDirection = "right",
  renderToast,
}: ToastHostProps) {
  const [toasts, setToasts] = React.useState<InternalToast[]>([]);

  const push = React.useCallback((toast: ToastDescriptor) => {
    const id = toast.id ?? `cw-v2-toast-${Math.random().toString(36).slice(2)}`;
    setToasts((prev) => [...prev, { ...toast, id, open: true }]);
    return id;
  }, []);

  const dismiss = React.useCallback((id: string) => {
    setToasts((prev) => prev.map((t) => (t.id === id ? { ...t, open: false } : t)));
  }, []);

  const dismissAll = React.useCallback(() => {
    setToasts((prev) => prev.map((t) => ({ ...t, open: false })));
  }, []);

  // Garbage-collect closed toasts after the exit animation has had a chance to
  // run. Radix already calls `onOpenChange(false)` when its internal close
  // logic fires, so this also covers swipe-to-dismiss.
  const handleOpenChange = (id: string, open: boolean) => {
    if (open) return;
    window.setTimeout(() => {
      setToasts((prev) => prev.filter((t) => t.id !== id));
    }, 200);
  };

  const context = React.useMemo<ToastContextValue>(
    () => ({ push, dismiss, dismissAll }),
    [push, dismiss, dismissAll],
  );

  return (
    <ToastContext.Provider value={context}>
      <ToastProvider duration={duration} swipeDirection={swipeDirection}>
        {children}
        {toasts.map((toast) =>
          renderToast ? (
            <React.Fragment key={toast.id}>{renderToast(toast)}</React.Fragment>
          ) : (
            <DefaultToast
              key={toast.id}
              toast={toast}
              onOpenChange={(open) => handleOpenChange(toast.id, open)}
            />
          ),
        )}
        <ToastViewport />
      </ToastProvider>
    </ToastContext.Provider>
  );
}

function DefaultToast({
  toast,
  onOpenChange,
}: {
  toast: InternalToast;
  onOpenChange: (open: boolean) => void;
}) {
  const variant = toast.variant ?? "neutral";
  const Icon = ICON_BY_VARIANT[variant];
  // Build the action row. The structured `actionLabel` / `secondaryActionLabel`
  // shortcuts render Buttons at size sm automatically, enforcing the visual
  // hierarchy: secondary action (link by default) on the left, primary action
  // (filled by default) on the right. Each is wrapped in a ToastAction so
  // Radix can still surface them to assistive tech.
  const primaryAltText = typeof toast.actionLabel === "string" ? toast.actionLabel : "Toast action";
  const secondaryAltText =
    typeof toast.secondaryActionLabel === "string"
      ? toast.secondaryActionLabel
      : "Toast secondary action";
  const hasStructuredAction = Boolean(toast.actionLabel) || Boolean(toast.secondaryActionLabel);
  const renderedActionRow = hasStructuredAction ? (
    <div className="cw-v2-toast__actions">
      {toast.secondaryActionLabel ? (
        <ToastAction altText={secondaryAltText} asChild>
          <Button
            size="sm"
            variant={toast.secondaryActionVariant ?? "link"}
            onClick={toast.onSecondaryAction}
          >
            {toast.secondaryActionLabel}
          </Button>
        </ToastAction>
      ) : null}
      {toast.actionLabel ? (
        <ToastAction altText={primaryAltText} asChild>
          <Button size="sm" variant={toast.actionVariant ?? "secondary"} onClick={toast.onAction}>
            {toast.actionLabel}
          </Button>
        </ToastAction>
      ) : null}
    </div>
  ) : null;

  return (
    <ToastRoot
      open={toast.open}
      onOpenChange={onOpenChange}
      duration={toast.duration}
      variant={variant}
    >
      <span className="cw-v2-toast__icon-wrap" aria-hidden>
        <Icon className="cw-v2-toast__icon" />
      </span>
      <div className="cw-v2-toast__body">
        {toast.title ? <ToastTitle>{toast.title}</ToastTitle> : null}
        {toast.description ? <ToastDescription>{toast.description}</ToastDescription> : null}
        {renderedActionRow}
      </div>
      {/* Custom `action` ReactNode escape hatch still sits in its own slot to
          the right of the body, for non-button trailing UI (Link, avatar, etc). */}
      {!hasStructuredAction && toast.action ? (
        <div className="cw-v2-toast__action-wrap">{toast.action}</div>
      ) : null}
      <ToastClose />
    </ToastRoot>
  );
}

/**
 * Imperative toast API. Must be called from a component rendered inside a
 * ToastHost.
 *
 * Returns helpers `info`, `success`, `warning`, `danger`, plus the lower-level
 * `push` / `dismiss` / `dismissAll`.
 */
export function useToast() {
  const context = React.useContext(ToastContext);
  if (!context) {
    throw new Error(
      "useToast must be used inside a <ToastHost>. Mount ToastHost near the root of your app.",
    );
  }
  const { push, dismiss, dismissAll } = context;

  return React.useMemo(() => {
    const make = (variant: ToastVariant) => (toast: Omit<ToastDescriptor, "variant">) =>
      push({ ...toast, variant });
    return {
      push,
      dismiss,
      dismissAll,
      info: make("info"),
      success: make("success"),
      warning: make("warning"),
      danger: make("danger"),
      neutral: make("neutral"),
    };
  }, [push, dismiss, dismissAll]);
}
